<template>
  <div class="pdf-viewer">
    <header class="pdf-viewer__header box-shadow" style="z-index: 1;">
      <v-btn @click.prevent.stop="togglePreview" v-if="!isScreenCapEnabled" justify="start">
        <v-icon>vertical_split</v-icon>
      </v-btn>
      <v-btn @click.prevent.stop="toggleScreenCap">{{ screenCaptureButtonText }} </v-btn>

      <v-btn
        v-if="isScreenCapEnabled && capturedImage !== null"
        @click.prevent.stop="handleAddImageToResource"
      >
        add To Resource
      </v-btn>

      <PDFZoom
        :scale="scale"
        @change="updateScale"
        @fit="updateFit"
        class="header-item"
        v-if="!isScreenCapEnabled"
      />

      <PDFPaginator
        v-model="currentPage"
        :pageCount="pageCount"
        class="header-item"
        v-if="!isScreenCapEnabled"
      />

      <slot name="header"></slot>
    </header>

    <PDFData
      v-if="url !== false"
      class="pdf-viewer__main"
      :url="url"
      @page-count="updatePageCount"
      @page-focus="updateCurrentPage"
      @document-rendered="onDocumentRendered"
      @document-errored="onDocumentErrored"
    >
      <template v-slot:preview="{ pages }">
        <PDFPreview
          v-show="isPreviewEnabled"
          class="pdf-viewer__preview"
          v-bind="{ pages, scale, currentPage, pageCount, isPreviewEnabled }"
        />
      </template>

      <template v-slot:document="{ pages }">
        <PDFDocument
          class="pdf-viewer__document"
          :class="{ 'preview-enabled': isPreviewEnabled }"
          v-bind="{
            pages,
            scale,
            optimalScale,
            fit,
            currentPage,
            pageCount,
            isPreviewEnabled
          }"
          @scale-change="updateScale"
          @selectExcerpt="$emit('selectExcerpt', $event)"
          @saveExcerpt="$emit('saveExcerpt', { ...$event, resourceId: resourceId })"
          @saveScreen="handleAddImageToResource"
        />
      </template>
    </PDFData>
  </div>
</template>

<script>
import PDFDocument from './PDFDocument';
import PDFData from './PDFData';
import PDFPaginator from './PDFPaginator';
import PDFPreview from './PDFPreview';
import PDFZoom from './PDFZoom';
import fb from '@/firebaseConfig';
import { mapState, mapMutations, mapActions } from 'vuex';

function floor(value, precision) {
  const multiplier = Math.pow(10, precision || 0);
  return Math.floor(value * multiplier) / multiplier;
}

export default {
  name: 'PDFViewer',

  components: {
    PDFDocument,
    PDFData,
    PDFPaginator,
    PDFPreview,
    PDFZoom
  },

  props: {
    resourceId: String,
    screenCaptureMode: {},
    captureSelection: {}
  },

  data() {
    return {
      scale: undefined,
      optimalScale: undefined,
      fit: undefined,
      currentPage: 1,
      pageCount: undefined,
      isPreviewEnabled: false,
      url: false
    };
  },
  computed: {
    ...mapState('components/pdfViewer', ['isScreenCapEnabled', 'capturedImage', 'capturedText']),
    ...mapState('resources', ['resources']),
    screenCaptureButtonText() {
      if (this.isScreenCapEnabled) {
        return 'Exit Screen Capture';
      } else {
        return 'Screen Grab';
      }
    }
  },
  methods: {
    ...mapMutations('components/pdfViewer', [
      'toggleScreenCapState',
      'setCapturedImage',
      'setCurrentResourceId'
    ]),
    ...mapActions('components/pdfViewer', ['addSelectionToResourceExcerpt', 'addImageToResource']),
    async setFilePath() {
      //this.loadingImage = true;
      let url;
      try {
        const fileRef = this.resources[this.resourceId].resourceData.fileRef;
        const fbFileRef = fb.storage.ref().child(fileRef);
        this.url = await fbFileRef.getDownloadURL();
        this.setCurrentResourceId({ resourceId: this.resourceId });
        this.$emit('ready');
      } catch (e) {
        console.log(e);
        this.url = '';
      }
      //this.loadingImage = false;
      //this.url = this.resources[this.resourceId].resourceData.url;
    },

    onDocumentRendered() {
      this.$emit('document-errored', this.url);
    },

    onDocumentErrored(e) {
      this.$emit('document-errored', e);
    },

    updateScale({ scale, isOptimal = false }) {
      const roundedScale = floor(scale, 2);
      if (isOptimal) this.optimalScale = roundedScale;
      this.scale = roundedScale;
    },

    updateFit(fit) {
      this.fit = fit;
    },

    updatePageCount(pageCount) {
      this.pageCount = pageCount;
    },

    updateCurrentPage(pageNumber) {
      this.currentPage = pageNumber;
    },

    togglePreview() {
      this.isPreviewEnabled = !this.isPreviewEnabled;
    },

    toggleScreenCap() {
      this.toggleScreenCapState();
    },

    handleCaptureText() {
      this.addSelectionToResourceExcerpt();
    },
    handleAddImageToResource() {
      const { url, pages, positions } = this.capturedImage;
      const excerptData = {
        imageUrl: url,
        positions,
        pages
      };
      this.$emit('saveScreen', {
        resourceId: this.resourceId,
        excerptData,
        source: 'pdfViewer',
        callback: () => {
          this.$emit('updateScreenCaptureMode', false);
        }
      });
      // this.addImageToResource()
      //  .then(clipId => {
      //     console.log('copy addedExcerpt');
      //     this.$emit('addedExcerpt', {
      //       clipId,
      //       excerpt: 'Screen Capture',
      //       resourceId: this.resource.id
      //     });
      //     this.toggleScreenCapState();
      //  }
    },
    scrollContent(id) {
      this.$vuetify.goTo(`.excerpt-${id}`, { container: '.pdf-document' });
    }
  },

  watch: {
    url() {
      this.currentPage = undefined;
    },
    filePath: function(val) {
      if (val) {
        this.loadPDF();
      }
    },
    screenCaptureMode: function() {
      console.log('capture mode');
      this.toggleScreenCapState();
    }
  },

  mounted() {
    console.log('mounted');
    this.setFilePath();
    document.body.classList.add('overflow-hidden');
  },

  destroyed() {
    this.setCurrentResourceId({ resourceId: null });
  }
};
</script>

<style scoped>
header {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 1em;
  position: relative;
  z-index: 99;
}
.header-item {
  margin: 0 2.5em;
}

.pdf-viewer .pdf-viewer__document,
.pdf-viewer .pdf-viewer__preview {
  top: 70px;
}

.pdf-viewer__preview {
  display: block;
  width: 15%;
  right: 85%;
}

.pdf-viewer__document {
  top: 70px;
  width: 100%;
  left: 0;
}

.pdf-viewer__document.preview-enabled {
  width: 85%;
  left: 15%;
}

@media print {
  header {
    display: none;
  }
}
</style>
